import React, { useState, useEffect, useCallback, useContext } from "react";
import toast from "react-hot-toast";
import moment from "moment";
import { CiSearch } from "react-icons/ci";
import { MdPreview, MdCheck, MdClose, MdDeleteOutline } from "react-icons/md";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import WalletRequestServices from "../../services/WalletRequestServices";
import { STATUS_OPTIONS } from "../../utils/utils";
import { AuthContext } from "../../context/AuthContext";
import AdminUsersServices from "../../services/AdminUsersServices";
import "../../styles/WalletManage.css";
import { Modal } from "react-bootstrap";
import { RiCloseLargeFill } from "react-icons/ri";
import { IoMdAdd } from "react-icons/io";
import ConfirmWalletDeleteModal from "./modals/ConfirmWalletDeleteModal";

const WalletManage = () => {
  const walletRequestApi = new WalletRequestServices();
  const adminSerApi = new AdminUsersServices();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [adminWalletBalance, setAdminWalletBalance] = useState(0.0);
  const [walletRequestList, setWalletRequestList] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState(null);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [fromDate, setFromDate] = useState(moment().format("YYYY-MM-DD"));
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD"));
  const [summary, setSummary] = useState({
    totalWalletAmount: 0,
    pendingCount: 0,
    pendingAmount: 0,
  });

  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [actionType, setActionType] = useState("APPROVE"); // APPROVE or REJECT
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const getAdminWalletBalance = useCallback(async () => {
    try {
      const response = await adminSerApi.addAdminWalletBalanceById();
      if (response.isSuccess) {
        setAdminWalletBalance(response?.data?.wallet_balance);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to get admin wallet balance",
      );
    }
  }, []);

  const getWalletRequestList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await walletRequestApi.getWalletRequestList(
        page,
        limit,
        searchFilter,
        statusFilter,
        fromDate,
        toDate,
      );
      if (response.isSuccess) {
        setWalletRequestList(response.data || []);
        setPagination(response.pagination || null);
        setSummary(
          response.summary || {
            totalWalletAmount: 0,
            pendingCount: 0,
            pendingAmount: 0,
          },
        );
      } else {
        toast.error(response.message || "Failed to get wallet request list");
      }
    } catch (error) {
      console.error("Error in getWalletRequestList:", error);
      toast.error(
        error?.response?.data?.message || "Failed to get wallet request list",
      );
    } finally {
      setLoading(false);
    }
  }, [page, limit, searchFilter, statusFilter, fromDate, toDate]);

  useEffect(() => {
    getAdminWalletBalance();
  }, [getAdminWalletBalance]);

  useEffect(() => {
    getWalletRequestList();
  }, [getWalletRequestList]);

  return (
    <div className="wallet-management">
      {/* Header: search, filters, summary */}
      <div className="wallet-header">
        <div className="wallet-header-left">
          <div className="wallet-search-wrap">
            <CiSearch className="wallet-search-icon" size={20} />
            <input
              type="text"
              placeholder="Search by user name or ID..."
              className="wallet-search"
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <select
            className="wallet-status-select"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || "all"} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <div className="wallet-date-filter-section">
            <input
              type="date"
              className="wallet-date-input"
              value={fromDate}
              onChange={(e) => {
                setFromDate(e.target.value);
                setPage(1);
              }}
            />
            <span className="wallet-date-separator">to</span>
            <input
              type="date"
              className="wallet-date-input"
              value={toDate}
              onChange={(e) => {
                setToDate(e.target.value);
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className="wallet-header-right">
          {user?.role_id !== 1 && (
            <>
              <div className="wallet-summary-card">
                <span className="wallet-summary-label">Wallet Amount</span>
                <span className="wallet-summary-value">
                  ₹{Number(adminWalletBalance || 0).toFixed(2)}
                </span>
              </div>
            </>
          )}
          <div className="wallet-summary-card">
            <span className="wallet-summary-label">Pending Requests</span>
            <span className="wallet-summary-value">
              <span className="grid-wallet-status-pending">
                {summary.pendingCount}
              </span>{" "}
              (₹{Number(summary.pendingAmount).toFixed(2)})
            </span>
          </div>

          {user?.role_id !== 1 && (
            <>
              <div>
                <button
                  className="wallet-add-wallet-amount-btn"
                  type="button"
                  onClick={() => {
                    setRequestModalOpen(true);
                  }}
                >
                  <IoMdAdd size={18} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Table / List */}
      <div className="wallet-content">
        {loading ? (
          <Loader message="Loading wallet list..." />
        ) : walletRequestList.length === 0 ? (
          <NoData
            message="No wallets found"
            subText="Try adjusting your search or status filter."
          />
        ) : (
          <>
            <div className="wallet-table-header">
              <div className="wallet-col-user">Requester</div>
              <div className="wallet-col-amount">Amount</div>
              <div className="wallet-col-date">Created</div>
              <div className="wallet-col-status">Status</div>
              <div className="wallet-col-action">Action</div>
            </div>

            <div className="wallet-list">
              {walletRequestList?.map((req) => {
                const isParent = req.parent_id === user?.user_id;
                const isRequester = req.requester_id === user?.user_id;
                const isPending = req.status === "PENDING";
                const canAct = isParent && isPending;
                const canCancel = isRequester && isPending;
                return (
                  <div className="wallet-row" key={req.id}>
                    <div className="wallet-col wallet-col-user">
                      <span className="wallet-label">Requester</span>
                      <div className="wallet-user-name">
                        {req.requester_name || "—"}
                      </div>
                      <div className="wallet-user-id">
                        [{req.requester_username || "—"}]
                      </div>
                    </div>
                    <div className="wallet-col wallet-col-amount">
                      <span className="wallet-label">Amount</span>
                      <div className="wallet-amount">
                        ₹{Number(req.amount || 0).toFixed(2)}
                      </div>
                    </div>
                    <div className="wallet-col wallet-col-date">
                      <span className="wallet-label">Created</span>
                      <div className="wallet-date">
                        {req.created_at
                          ? moment(req.created_at).format(
                              "DD MMM YYYY, hh:mm A",
                            )
                          : "—"}
                      </div>
                    </div>
                    <div className="wallet-col wallet-col-status">
                      <span className="wallet-label">Status</span>
                      <span
                        className={`grid-wallet-status-${req.status?.toLowerCase()}`}
                      >
                        {req.status}
                      </span>
                    </div>
                    <div className="wallet-col wallet-col-action">
                      <span className="wallet-label">Action</span>
                      <div className="wallet-actions">
                        <button
                          className="wallet-icon-btn"
                          title="View details"
                          onClick={() => {
                            setSelectedDetail(req);
                            setDetailModalOpen(true);
                          }}
                        >
                          <MdPreview size={18} />
                        </button>
                        {canAct && (
                          <>
                            <button
                              className="wallet-icon-btn approve"
                              title="Approve"
                              onClick={() => {
                                setSelectedRequest(req);
                                setActionType("APPROVE");
                                setActionModalOpen(true);
                              }}
                            >
                              <MdCheck size={18} />
                            </button>
                            <button
                              className="wallet-icon-btn reject"
                              title="Reject"
                              onClick={() => {
                                setSelectedRequest(req);
                                setActionType("REJECT");
                                setActionModalOpen(true);
                              }}
                            >
                              <MdClose size={18} />
                            </button>
                          </>
                        )}
                        {canCancel && (
                          <button
                            className="wallet-icon-btn reject"
                            title="Delete"
                            onClick={() => {
                              setSelectedRequest(req);
                              setDeleteModalOpen(true);
                            }}
                          >
                            <MdDeleteOutline size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {pagination && pagination.totalPages > 1 && (
              <div className="modern-pagination">
                {/* Limit Selector */}
                <div className="mp-limit">
                  <span>Show</span>
                  <select
                    value={limit}
                    onChange={(e) => setLimit(e.target.value)}
                  >
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                </div>

                {/* Navigation */}
                <div className="mp-controls">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    Prev
                  </button>

                  <div className="mp-info">
                    Page <strong>{pagination.page}</strong> of{" "}
                    <strong>{pagination.totalPages}</strong>
                  </div>

                  <button
                    disabled={page === pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next
                  </button>
                </div>

                {/* Jump to Page */}
                <div className="mp-jump">
                  <select
                    value={page}
                    onChange={(e) => setPage(e.target.value)}
                  >
                    {Array.from({ length: pagination.totalPages }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        Page {i + 1}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <WalletRequestModal
        show={requestModalOpen}
        onClose={() => requestModalOpen && setRequestModalOpen(false)}
        onSuccess={() => {
          setRequestModalOpen(false);
          getWalletRequestList();
        }}
        api={walletRequestApi}
        loading={loading}
      />

      <WalletActionModal
        show={actionModalOpen}
        onClose={() => !actionLoading && setActionModalOpen(false)}
        type={actionType}
        request={selectedRequest}
        loading={actionLoading}
        onConfirm={async (adminRemark) => {
          if (!selectedRequest) return;
          setActionLoading(true);
          try {
            if (actionType === "APPROVE") {
              const res = await walletRequestApi.approveWalletRequest({
                id: selectedRequest.id,
                admin_remark: adminRemark,
              });
              if (res.isSuccess) {
                toast.success(res.message || "Request approved");
                await getAdminWalletBalance();
                await getWalletRequestList();
                setActionModalOpen(false);
              } else {
                toast.error(res.message || "Failed to approve");
              }
            } else {
              const res = await walletRequestApi.rejectWalletRequest({
                id: selectedRequest.id,
                admin_remark: adminRemark,
              });
              if (res.isSuccess) {
                toast.success(res.message || "Request rejected");
                await getWalletRequestList();
                setActionModalOpen(false);
              } else {
                toast.error(res.message || "Failed to reject");
              }
            }
          } catch (error) {
            toast.error(
              error?.response?.data?.message ||
                "Failed to update wallet request",
            );
          } finally {
            setActionLoading(false);
          }
        }}
      />

      <WalletDetailModal
        show={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        request={selectedDetail}
        loading={detailLoading}
      />

      <ConfirmWalletDeleteModal
        show={deleteModalOpen}
        onClose={() => !deleteLoading && setDeleteModalOpen(false)}
        loading={deleteLoading}
        requesterName={selectedRequest?.requester_name}
        amount={selectedRequest?.amount}
        onConfirm={async () => {
          if (!selectedRequest) return;
          setDeleteLoading(true);
          try {
            const res = await walletRequestApi.deleteWalletRequest(
              selectedRequest.id,
            );
            if (res.isSuccess) {
              toast.success(res.message || "Wallet request deleted");
              await getWalletRequestList();
              setDeleteModalOpen(false);
              setSelectedRequest(null);
            } else {
              toast.error(res.message || "Failed to delete wallet request");
            }
          } catch (error) {
            toast.error(
              error?.response?.data?.message ||
                "Failed to delete wallet request",
            );
          } finally {
            setDeleteLoading(false);
          }
        }}
      />
    </div>
  );
};

export default WalletManage;

const WalletRequestModal = ({ show, onClose, onSuccess, api }) => {
  const [amount, setAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    const value = Number(amount);
    if (!value || value <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    setLoading(true);
    try {
      const res = await api.createWalletRequest({ amount: value, remark });
      if (res.isSuccess) {
        toast.success(res.message || "Wallet request created");
        setAmount("");
        setRemark("");
        onSuccess && onSuccess();
      } else {
        toast.error(res.message || "Failed to create wallet request");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message || "Failed to create wallet request",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="sm"
    >
      <div className="wm-modal-header">
        <h5 className="modal-title">New Wallet Request</h5>
        <button
          type="button"
          className="wm-modal-close"
          onClick={onClose}
          disabled={loading}
        >
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />
      <Modal.Body>
        <div className="mb-2">
          <label className="form-label">Amount</label>
          <input
            type="number"
            className="form-control"
            min={0}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={loading}
          />
        </div>
        <div className="mb-2">
          <label className="form-label">Remark (optional)</label>
          <textarea
            className="form-control"
            rows={3}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            disabled={loading}
          />
        </div>
      </Modal.Body>
      <div className="modal-footer border-0 justify-content-center">
        <button
          className="btn btn-light px-4"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary px-4"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? "Saving..." : "Submit Request"}
        </button>
      </div>
    </Modal>
  );
};

const WalletActionModal = ({
  show,
  onClose,
  type,
  request,
  loading,
  onConfirm,
}) => {
  const [remark, setRemark] = useState("");

  useEffect(() => {
    if (!show) {
      setRemark("");
    }
  }, [show]);

  if (!request) return null;

  const title =
    type === "APPROVE" ? "Approve Wallet Request" : "Reject Wallet Request";

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="sm"
    >
      <div className="modal-header border-0 pb-0">
        <h5 className="modal-title">{title}</h5>
        <button
          type="button"
          className="wm-modal-close"
          onClick={onClose}
          disabled={loading}
        >
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />
      <Modal.Body>
        <p>
          <strong>Requester:</strong> {request.requester_name} (
          {request.requester_game_id || `ID: ${request.requester_id}`})
        </p>
        <p>
          <strong>Amount:</strong> ₹{Number(request.amount || 0).toFixed(2)}
        </p>
        <p>
          <strong>Original Remark:</strong> {request.remark || "—"}
        </p>
        <div className="mb-2">
          <label className="form-label">
            {type === "APPROVE" ? "Approval Remark" : "Rejection Remark"}
          </label>
          <textarea
            className="form-control"
            rows={3}
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            disabled={loading}
          />
        </div>
      </Modal.Body>
      <div className="modal-footer border-0 justify-content-center">
        <button
          className="btn btn-light px-4"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          className={`btn px-4 ${
            type === "APPROVE" ? "btn-success" : "btn-danger"
          }`}
          onClick={() => onConfirm(remark)}
          disabled={loading}
        >
          {loading
            ? type === "APPROVE"
              ? "Approving..."
              : "Rejecting..."
            : type === "APPROVE"
              ? "Approve"
              : "Reject"}
        </button>
      </div>
    </Modal>
  );
};

const WalletDetailModal = ({ show, onClose, request, loading }) => {
  if (!request) return null;

  const createdAt = request.created_at
    ? moment(request.created_at).format("DD MMM YYYY, hh:mm A")
    : "—";
  const processedAt = request.processed_at
    ? moment(request.processed_at).format("DD MMM YYYY, hh:mm A")
    : "—";

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      backdrop="static"
      keyboard={false}
      size="sm"
    >
      <div className="wm-modal-header">
        <h5 className="modal-title">Wallet Request Details</h5>
        <button
          type="button"
          className="wm-modal-close"
          onClick={onClose}
          disabled={loading}
        >
          <RiCloseLargeFill size={20} />
        </button>
      </div>
      <hr className="my-1" />
      <Modal.Body>
        <div className="wallet-detail-grid">
          <div className="wallet-detail-row">
            <span className="wallet-detail-label">Requester</span>
            <span className="wallet-detail-value">
              <div className="text-capitalize">{request.requester_name}</div>
              [{request.requester_username || ""}]
            </span>
          </div>
          <div className="wallet-detail-row">
            <span className="wallet-detail-label">Amount</span>
            <span className="wallet-detail-value">
              ₹{Number(request.amount || 0).toFixed(2)}
            </span>
          </div>
          <div className="wallet-detail-row">
            <span className="wallet-detail-label">Status</span>
            <span
              className={`wallet-status badge-${request.status?.toLowerCase()}`}
            >
              {request.status}
            </span>
          </div>
          <div className="wallet-detail-row">
            <span className="wallet-detail-label">Created At</span>
            <span className="wallet-detail-value">{createdAt}</span>
          </div>
          <div className="wallet-detail-row">
            <span className="wallet-detail-label">Processed At</span>
            <span className="wallet-detail-value">{processedAt}</span>
          </div>
          <div className="wallet-detail-row">
            <span className="wallet-detail-label">Requester Remark</span>
            <div className="wallet-detail-value">{request.remark || "—"}</div>
          </div>
          <div className="wallet-detail-row">
            <span className="wallet-detail-label">Admin Remark</span>
            <div className="wallet-detail-value">
              {request.admin_remark || "—"}
            </div>
          </div>
          <div className="wallet-detail-row">
            <span className="wallet-detail-label">Parent</span>
            <span className="wallet-detail-value-parent">
              {request.parent_name || "—"}
            </span>
          </div>
        </div>
      </Modal.Body>
      <div className="modal-footer border-0 justify-content-center">
        <button
          className="btn btn-light px-4"
          onClick={onClose}
          disabled={loading}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};
